import React, { useState, useEffect, useRef } from 'react';
import {
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonDatetime,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonIcon,
    IonSpinner,
    IonPopover,
    IonAvatar
} from '@ionic/react';
import { calendar, close, pencil } from 'ionicons/icons';
import styles from './TaskModal.module.scss';
import { TaskItem, TaskModalProps } from '@rsmaids/shared/types/tasks';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getCachedContact } from '../utils/contactCache';
import { Contact } from '@rsmaids/shared/types/contacts';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';
import i18n from '../i18n';


const TaskModal: React.FC<TaskModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    contactId,
    title = i18n.t('components.TaskModal.addTask')
}) => {
  const { t } = useTranslation();
    const [task, setTask] = useState<TaskItem>({
            id: initialData?.id || `task-${new Date().getTime()}`,
            title: initialData?.title || '',
            dueDate: initialData?.dueDate || new Date().toISOString(),
            priority: initialData?.priority || 'medium',
            status: initialData?.status || 'pending',
            assignedToId: '',
            contactId: contactId || '',
            completedAt: initialData?.completedAt || null
        });

    // Keep notes as a separate state
    const [notes, setNotes] = useState<string>(
        initialData?.notes || ''
    );

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [isContactSelectOpen, setIsContactSelectOpen] = useState(false);
    const datePickerRef = useRef<HTMLIonPopoverElement>(null);
    const dataService = DataServiceFactory.getService();
    const datePickerId = `date-picker-${task.id}`;
    const staffMembers = [
        { id: 'staff1', name: 'Staff Member 1' },
        { id: 'staff2', name: 'Staff Member 2' },
        { id: 'staff3', name: 'Staff Member 3' }
    ];

    // Fetch contacts and selected contact when component mounts
    useEffect(() => {
        // Fetch all contacts for the dropdown
        dataService.contacts.getAllContacts().then(allContacts => {
            setContacts(allContacts as unknown as Contact[]);
        });

        // If contactId is provided, fetch that contact
        if (contactId) {
            getCachedContact(contactId, () => dataService.contacts.getContactById(contactId))
                .then(result => {
                    if (result.data) {
                        setSelectedContact(result.data);
                    }
                });
        }
    }, [contactId, dataService.contacts]);

    const handleChange = (
        e: CustomEvent,
        field: keyof TaskItem
    ) => {
        const value = e.detail.value;
        setTask({ ...task, [field]: value });

        // Clear validation error when user types in title
        if (field === 'title' && value) {
            setValidationError('');
        }
    };

    const handleSave = async () => {
        if (!task.title.trim()) {
            setValidationError(t('components.TaskModal.taskTitleIsRequired'));
            return;
        }

        if (!task.contactId) {
            setValidationError(t('components.TaskModal.pleaseSelectAContactForThis'));
            return;
        }

        try {
            setIsSaving(true);
            // Include notes, assignedTo, and contactId as part of the task data
            const taskData: TaskItem = {
                ...task,
                notes,
                contactId: selectedContact?.id || task.contactId
            };
            await onSave(taskData);
            onClose();
        } catch (error) {
            console.error('Error saving task:', error);
            setValidationError(t('components.TaskModal.failedToSaveTaskPleaseTry'));
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (isoString: string | null) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleDateString(intlLocale(), {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Handle date selection
    const handleDateChange = (e: CustomEvent) => {
        const selectedValue = e.detail.value;
        if (selectedValue) {
            setTask({
                ...task,
                dueDate: selectedValue
            });
        }
        setIsDatePickerOpen(false);
    };

    // Handle contact selection
    const handleContactChange = (e: CustomEvent) => {
        const contactId = e.detail.value;
        if (contactId) {
            const contact = contacts.find(c => c.id === contactId);
            if (contact) {
                setSelectedContact(contact);
                setTask({
                    ...task,
                    contactId
                });
            }
        }
        setIsContactSelectOpen(false);
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.taskModal}>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton onClick={onClose}>
                                <IonIcon icon={close} />
                            </IonButton>
                        </IonButtons>
                        <IonTitle>{title}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={handleSave} strong disabled={isSaving}>
                                {isSaving ? <IonSpinner name="dots" /> : t('components.TaskModal.save')}
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent className={styles.taskModalContent}>
                    {validationError && (
                        <IonItem lines="none" className={styles.errorItem}>
                            <IonLabel color="danger" className={styles.errorText}>
                                {validationError}
                            </IonLabel>
                        </IonItem>
                    )}

                    {/* Contact selection section */}
                    <IonItem className={styles.formItem}>
                        <IonLabel position="stacked">{t('components.TaskModal.contact')}</IonLabel>
                        <IonItem lines="none" className={styles.contactSelectItem}>
                            {selectedContact ? (
                                <>
                                    <IonAvatar slot="start" className={styles.contactAvatar}>
                                        <img 
                                            src={selectedContact.avatar || getDefaultAvatar(`${selectedContact.firstName} ${selectedContact.lastName}`)} 
                                            alt={`${selectedContact.firstName} ${selectedContact.lastName}`} 
                                        />
                                    </IonAvatar>
                                    <IonLabel>
                                        {`${selectedContact.firstName} ${selectedContact.lastName}`}
                                    </IonLabel>
                                </>
                            ) : (
                                <IonLabel color="medium">{t('components.TaskModal.selectAContact')}</IonLabel>
                            )}
                            <IonButton 
                                fill="clear" 
                                slot="end" 
                                onClick={() => setIsContactSelectOpen(true)}
                                disabled={!!contactId} // Disable if contactId is provided
                                className={contactId ? styles.disabledEditButton : styles.editButton}
                            >
                                <IonIcon icon={pencil} />
                            </IonButton>
                        </IonItem>
                    </IonItem>

                    <IonItem className={styles.formItem}>
                        <IonLabel position="stacked">{t('components.TaskModal.title')}</IonLabel>
                        <IonInput
                            value={task.title}
                            placeholder={t('components.TaskModal.enterTaskTitle')}
                            onIonChange={(e) => handleChange(e, 'title')}
                            className={styles.taskInput}
                        />
                    </IonItem>

                    <IonItem className={styles.formItem}>
                        <IonLabel position="stacked">{t('components.TaskModal.notes')}</IonLabel>
                        <IonTextarea
                            value={notes}
                            placeholder={t('components.TaskModal.enterTaskNotes')}
                            rows={4}
                            onIonChange={(e) => setNotes(e.detail.value ?? '')}
                            className={styles.taskTextarea}
                        />
                    </IonItem>
                    <IonItem className={styles.formItem} button detail={false} id={datePickerId} onClick={() => setIsDatePickerOpen(true)}>
                        <IonLabel position="stacked">{t('components.TaskModal.dueDate')}</IonLabel>
                        <IonInput
                            readonly
                            value={formatDate(task.dueDate)}
                            className={styles.dateInput}
                        />
                        <IonIcon icon={calendar} slot="end" color="medium" />
                    </IonItem>

                    <IonItem className={styles.formItem}>
                        <IonLabel position="stacked">{t('components.TaskModal.assignTo')}</IonLabel>
                        <IonSelect
                            value={task.assignedToId}
                            onIonChange={(e) => handleChange(e, 'assignedToId')}
                            interface="action-sheet"
                            placeholder={t('components.TaskModal.selectStaffMember')}
                            className={styles.staffSelect}
                        >
                            {staffMembers.map(staff => (
                                <IonSelectOption key={staff.id} value={staff.id}>
                                    {staff.name}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonItem>
                </IonContent>
            </IonModal>

            {/* Date Picker Popover */}
            <IonPopover
                ref={datePickerRef}
                isOpen={isDatePickerOpen}
                onDidDismiss={() => setIsDatePickerOpen(false)}
                className={styles.datePickerPopover}
            >
                <IonContent>
                    <IonDatetime locale={intlLocale()}
                        value={task.dueDate}
                        presentation="date"
                        onIonChange={handleDateChange}
                    />
                    <IonButtons className={styles.dateButtons}>
                        <IonButton onClick={() => setIsDatePickerOpen(false)}>{t('components.TaskModal.cancel')}
                        </IonButton>
                        <IonButton
                            strong
                            onClick={() => setIsDatePickerOpen(false)}
                        >{t('components.TaskModal.done')}
                        </IonButton>
                    </IonButtons>
                </IonContent>
            </IonPopover>

            {/* Contact Selection Popover */}
            <IonPopover
                isOpen={isContactSelectOpen}
                onDidDismiss={() => setIsContactSelectOpen(false)}
                className={styles.contactPickerPopover}
            >
                <IonContent>
                    <IonItem lines="full">
                        <IonLabel>
                            <h2>{t('components.TaskModal.selectContact')}</h2>
                        </IonLabel>
                    </IonItem>
                    <IonSelect
                        value={task.contactId}
                        onIonChange={handleContactChange}
                        interface="popover"
                        className={styles.contactSelect}
                    >
                        {contacts.map(contact => (
                            <IonSelectOption key={contact.id} value={contact.id}>
                                {`${contact.firstName} ${contact.lastName}`}
                            </IonSelectOption>
                        ))}
                    </IonSelect>
                </IonContent>
            </IonPopover>
        </>
    );
};

export default TaskModal;
